import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Factory, Package, Users, Download, TrendingUp, FileSpreadsheet, FileText } from "lucide-react";
import { ResponsiveContainer, LineChart, Line as RLine, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { useSortableTable, SortableTh } from "@/components/SortableTable";

const MONTHS = [
  "January","February","March","April","May","June","July","August","September","October","November","December",
];

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function KPI({ icon: Icon, label, value, tint = "slate" }) {
  const tints = {
    brand: "text-brand", emerald: "text-emerald-700", blue: "text-blue-700", amber: "text-amber-700", slate: "text-slate-700",
  };
  return (
    <Card className="p-4 border-slate-200 rounded-md bg-white">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${tints[tint]}`} />
        <div className="text-[10.5px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
      </div>
      <div className="text-2xl font-heading font-bold mt-1">{value}</div>
    </Card>
  );
}

function fmt(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export default function PPMDReport({ lines }) {
  const now = new Date();
  const [mode, setMode] = useState("day");
  const [date, setDate] = useState(todayStr());
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [lineId, setLineId] = useState("__all__");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const ppmdColumns = useMemo(() => ({
    line:        { get: (r) => r.line_name || "", type: "text" },
    days_active: { get: (r) => Number(r.days_active || 0), type: "number" },
    output:      { get: (r) => Number(r.output || 0), type: "number" },
    machine_ops: { get: (r) => Number(r.machine_ops || 0), type: "number" },
    helpers:     { get: (r) => Number(r.helpers || 0), type: "number" },
    manpower:    { get: (r) => Number(r.manpower || 0), type: "number" },
    ppmd:        { get: (r) => (r.ppmd == null ? null : Number(r.ppmd)), type: "number" },
  }), []);
  const { sortedRows: ppmdRows, sortKey: ppmdSortKey, sortDir: ppmdSortDir, cycleSort: ppmdCycle } = useSortableTable(data?.rows || [], ppmdColumns, { defaultKey: "ppmd", defaultDir: "desc", storageKey: "sort:opsiq:ppmd" });

  const years = useMemo(() => {
    const y0 = now.getFullYear();
    return Array.from(new Set([y0 - 1, y0, y0 + 1, year])).sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { mode };
      if (mode === "day") params.date = date;
      else { params.year = year; params.month = month; }
      if (lineId && lineId !== "__all__") params.line_id = lineId;
      const { data } = await api.get("/line-tracking/ppmd", { params });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load PPMD");
      setData(null);
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, date, year, month, lineId]);

  const scopeLabel = mode === "day"
    ? new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    : `${MONTHS[month - 1]} ${year}`;

  const exportCSV = () => {
    if (!data) return;
    const headers = ["Line", "Days Active", "Total Output", "Machine Op Man-Days", "Helper Man-Days", "Total Man-Days", "PPMD"];
    const rows = (data.rows || []).map((r) => [
      r.line_name, r.days_active, r.output, r.machine_ops, r.helpers, r.manpower, r.ppmd ?? "",
    ]);
    const t = data.totals || {};
    rows.push(["TOTAL", "", t.output ?? "", t.machine_ops ?? "", t.helpers ?? "", t.manpower ?? "", t.ppmd ?? ""]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ppmd-${mode}-${mode === "day" ? date : `${year}-${String(month).padStart(2,"0")}`}.csv`;
    a.click();
    toast.success("Exported");
  };

  const fileStem = () => `ppmd-${mode}-${mode === "day" ? date : `${year}-${String(month).padStart(2,"0")}`}`;

  const exportExcel = () => {
    if (!data) return;
    const t = data.totals || {};
    const summarySheet = [
      ["Line", "Days Active", "Total Output", "Machine Op Man-Days", "Helper Man-Days", "Total Man-Days", "PPMD"],
      ...(data.rows || []).map((r) => [r.line_name, r.days_active, r.output, r.machine_ops, r.helpers, r.manpower, r.ppmd ?? ""]),
      ["TOTAL", "", t.output ?? "", t.machine_ops ?? "", t.helpers ?? "", t.manpower ?? "", t.ppmd ?? ""],
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      [`Ops IQ · PPMD Report · ${scopeLabel}`],
      [`Formula: PPMD = Total Pcs Produced ÷ (Machine Operators + Table Ops & Helpers)`],
      [],
      ...summarySheet,
    ]);
    ws["!cols"] = [{ wch: 22 }, { wch: 12 }, { wch: 14 }, { wch: 20 }, { wch: 18 }, { wch: 16 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, ws, "Line-wise PPMD");
    if (mode === "month" && lineId !== "__all__" && (data.per_day || []).length > 0) {
      const perDay = [
        ["Date", "Output", "Machine Ops", "Helpers", "Manpower", "PPMD"],
        ...data.per_day.map((r) => [r.date, r.output, r.machine_ops, r.helpers, r.manpower, r.ppmd ?? ""]),
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(perDay);
      ws2["!cols"] = [{ wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 10 }, { wch: 12 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, ws2, "Day-by-day");
    }
    XLSX.writeFile(wb, `${fileStem()}.xlsx`);
    toast.success("Excel exported");
  };

  const exportPDF = () => {
    if (!data) return;
    const t = data.totals || {};
    const lineName = lineId === "__all__" ? "All lines" : ((lines || []).find((l) => l.id === lineId)?.name || "Line");
    const rowsHtml = (data.rows || []).map((r) => `
      <tr>
        <td>${r.line_name}</td>
        ${mode === "month" ? `<td class="num">${r.days_active}</td>` : ""}
        <td class="num">${fmt(r.output)}</td>
        <td class="num">${fmt(r.machine_ops)}</td>
        <td class="num">${fmt(r.helpers)}</td>
        <td class="num">${fmt(r.manpower)}</td>
        <td class="num strong brand">${r.ppmd != null ? fmt(r.ppmd) : "—"}</td>
      </tr>`).join("");
    const perDayHtml = (mode === "month" && lineId !== "__all__" && (data.per_day || []).length > 0) ? `
      <h2>Day-by-day · ${lineName}</h2>
      <table>
        <thead><tr><th>Date</th><th class="num">Output</th><th class="num">Machine Ops</th><th class="num">Helpers</th><th class="num">Manpower</th><th class="num">PPMD</th></tr></thead>
        <tbody>${data.per_day.map((r) => `
          <tr>
            <td>${r.date}</td>
            <td class="num">${fmt(r.output)}</td>
            <td class="num">${fmt(r.machine_ops)}</td>
            <td class="num">${fmt(r.helpers)}</td>
            <td class="num">${fmt(r.manpower)}</td>
            <td class="num strong brand">${r.ppmd != null ? fmt(r.ppmd) : "—"}</td>
          </tr>`).join("")}
        </tbody>
      </table>` : "";

    const w = window.open("", "_blank", "width=1024,height=768");
    if (!w) { toast.error("Popup blocked — allow popups to print"); return; }
    w.document.write(`<!doctype html><html><head><title>PPMD · ${scopeLabel}</title>
      <style>
        body{ font-family: Arial, sans-serif; color:#0F172A; padding:24px; }
        h1{ font-size:20px; margin:0 0 4px 0 }
        h2{ font-size:14px; margin:16px 0 8px 0; color:#7C1D3A }
        .meta{ color:#64748B; font-size:12px }
        .kpis{ display:grid; grid-template-columns: repeat(5, 1fr); gap:12px; margin: 16px 0 }
        .kpi{ border:1px solid #E5E7EB; border-radius:6px; padding:10px }
        .kpi .lbl{ font-size:10px; text-transform:uppercase; letter-spacing:.06em; color:#64748B }
        .kpi .val{ font-size:18px; font-weight:700; margin-top:4px }
        table{ width:100%; border-collapse: collapse; font-size:12px; margin-top:8px }
        th, td{ border-bottom:1px solid #E5E7EB; padding:6px 8px; text-align:left }
        th{ background:#F8FAFC; text-transform:uppercase; font-size:10.5px; letter-spacing:.05em; color:#475569 }
        td.num, th.num{ text-align:right; font-family: ui-monospace, Menlo, monospace }
        .strong{ font-weight:700 }
        .brand{ color:#7C1D3A }
        tfoot td{ font-weight:700; background:#F8FAFC }
        .foot{ margin-top:24px; font-size:10px; color:#94A3B8; text-align:right }
      </style></head><body>
      <h1>Ops IQ · PPMD Report</h1>
      <div class="meta">${scopeLabel} · ${lineName} · Formula: PPMD = Total Pcs Produced ÷ (Machine Operators + Table Ops &amp; Helpers)</div>
      <div class="kpis">
        <div class="kpi"><div class="lbl">Total Output</div><div class="val">${fmt(t.output)}</div></div>
        <div class="kpi"><div class="lbl">Machine Op Man-Days</div><div class="val">${fmt(t.machine_ops)}</div></div>
        <div class="kpi"><div class="lbl">Helper Man-Days</div><div class="val">${fmt(t.helpers)}</div></div>
        <div class="kpi"><div class="lbl">Total Manpower</div><div class="val">${fmt(t.manpower)}</div></div>
        <div class="kpi"><div class="lbl">Overall PPMD</div><div class="val" style="color:#7C1D3A">${t.ppmd != null ? fmt(t.ppmd) : "—"}</div></div>
      </div>
      <h2>Line-wise PPMD</h2>
      <table>
        <thead><tr>
          <th>Line</th>
          ${mode === "month" ? `<th class="num">Days Active</th>` : ""}
          <th class="num">Output</th>
          <th class="num">Machine Ops${mode === "month" ? " Man-Days" : ""}</th>
          <th class="num">Helpers${mode === "month" ? " Man-Days" : ""}</th>
          <th class="num">Total${mode === "month" ? " Man-Days" : " Manpower"}</th>
          <th class="num">PPMD</th>
        </tr></thead>
        <tbody>${rowsHtml || `<tr><td colspan="${mode === "month" ? 7 : 6}" style="text-align:center; color:#94A3B8; padding:24px">No data.</td></tr>`}</tbody>
        <tfoot><tr>
          <td>TOTAL</td>
          ${mode === "month" ? `<td></td>` : ""}
          <td class="num">${fmt(t.output)}</td>
          <td class="num">${fmt(t.machine_ops)}</td>
          <td class="num">${fmt(t.helpers)}</td>
          <td class="num">${fmt(t.manpower)}</td>
          <td class="num brand">${t.ppmd != null ? fmt(t.ppmd) : "—"}</td>
        </tr></tfoot>
      </table>
      ${perDayHtml}
      <div class="foot">Generated ${new Date().toLocaleString()} · Ops IQ · BBS Global</div>
      <script>window.onload = () => { setTimeout(() => window.print(), 250); }</script>
      </body></html>`);
    w.document.close();
    toast.success("PDF ready");
  };

  return (
    <div className="space-y-4" data-testid="ppmd-report">
      <Card className="border-slate-200 rounded-md bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">View</label>
            <div className="mt-1 inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="ppmd-mode-toggle">
              <button
                type="button"
                className={`px-3 h-10 text-[13px] font-semibold ${mode === "day" ? "bg-brand text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                onClick={() => setMode("day")}
                data-testid="ppmd-mode-day"
              >Day-wise</button>
              <button
                type="button"
                className={`px-3 h-10 text-[13px] font-semibold border-l border-slate-200 ${mode === "month" ? "bg-brand text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                onClick={() => setMode("month")}
                data-testid="ppmd-mode-month"
              >Month-wise</button>
            </div>
          </div>

          {mode === "day" ? (
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1"><Calendar className="h-3 w-3" /> Date</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 mt-1 w-[170px]" data-testid="ppmd-date" />
            </div>
          ) : (
            <>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Month</label>
                <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                  <SelectTrigger data-testid="ppmd-month" className="mt-1 h-10 w-[150px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Year</label>
                <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                  <SelectTrigger data-testid="ppmd-year" className="mt-1 h-10 w-[110px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </>
          )}

          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Line</label>
            <Select value={lineId} onValueChange={setLineId}>
              <SelectTrigger data-testid="ppmd-line" className="mt-1 h-10 w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All lines</SelectItem>
                {(lines || []).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={exportCSV} disabled={!data} data-testid="ppmd-export-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
            <Button size="sm" variant="outline" onClick={exportExcel} disabled={!data} data-testid="ppmd-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</Button>
            <Button size="sm" variant="outline" onClick={exportPDF} disabled={!data} data-testid="ppmd-export-pdf"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
          </div>
        </div>
        <div className="mt-2 text-[12px] text-slate-500">
          <span className="font-semibold text-slate-700">PPMD</span> = Total Pcs Produced ÷ (Machine Operators + Table Ops &amp; Helpers). QC excluded from the denominator.
        </div>
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI icon={Factory} tint="brand" label={mode === "day" ? "Date" : "Period"} value={scopeLabel} />
            <KPI icon={Package} tint="emerald" label="Total Output" value={fmt(data.totals?.output)} />
            <KPI icon={Users} tint="blue" label="Machine Op Man-Days" value={fmt(data.totals?.machine_ops)} />
            <KPI icon={Users} tint="amber" label="Helper Man-Days" value={fmt(data.totals?.helpers)} />
            <KPI icon={TrendingUp} tint="emerald" label="Overall PPMD" value={data.totals?.ppmd != null ? fmt(data.totals.ppmd) : "—"} />
          </div>

          <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="font-heading font-semibold text-[15px]">Line-wise PPMD · {scopeLabel}</div>
              <div className="text-[11.5px] text-slate-500">{data.totals?.lines_reporting || 0} line(s) reporting</div>
            </div>
            <div className="overflow-x-auto">
              <table className="opsiq-table" data-testid="ppmd-table">
                <thead>
                  <tr>
                    <SortableTh label="Line" colKey="line" sortKey={ppmdSortKey} sortDir={ppmdSortDir} onClick={ppmdCycle} />
                    {mode === "month" && <SortableTh label="Days Active" colKey="days_active" sortKey={ppmdSortKey} sortDir={ppmdSortDir} onClick={ppmdCycle} align="right" />}
                    <SortableTh label="Output (pcs)" colKey="output" sortKey={ppmdSortKey} sortDir={ppmdSortDir} onClick={ppmdCycle} align="right" />
                    <SortableTh label={`Machine Ops${mode === "month" ? " Man-Days" : ""}`} colKey="machine_ops" sortKey={ppmdSortKey} sortDir={ppmdSortDir} onClick={ppmdCycle} align="right" />
                    <SortableTh label={`Helpers${mode === "month" ? " Man-Days" : ""}`} colKey="helpers" sortKey={ppmdSortKey} sortDir={ppmdSortDir} onClick={ppmdCycle} align="right" />
                    <SortableTh label={`Total ${mode === "month" ? "Man-Days" : "Manpower"}`} colKey="manpower" sortKey={ppmdSortKey} sortDir={ppmdSortDir} onClick={ppmdCycle} align="right" />
                    <SortableTh label="PPMD" colKey="ppmd" sortKey={ppmdSortKey} sortDir={ppmdSortDir} onClick={ppmdCycle} align="right" />
                  </tr>
                </thead>
                <tbody>
                  {ppmdRows.map((r) => (
                    <tr key={r.line_id} data-testid={`ppmd-row-${r.line_id}`}>
                      <td className="font-semibold">{r.line_name}</td>
                      {mode === "month" && <td className="text-right font-mono">{r.days_active}</td>}
                      <td className="text-right font-mono">{fmt(r.output)}</td>
                      <td className="text-right font-mono text-blue-700">{fmt(r.machine_ops)}</td>
                      <td className="text-right font-mono text-amber-700">{fmt(r.helpers)}</td>
                      <td className="text-right font-mono">{fmt(r.manpower)}</td>
                      <td className="text-right font-mono font-bold text-brand">{r.ppmd != null ? fmt(r.ppmd) : "—"}</td>
                    </tr>
                  ))}
                  {ppmdRows.length === 0 && (
                    <tr><td colSpan={mode === "month" ? 7 : 6} className="text-center py-10 text-slate-500">
                      {loading ? "Loading…" : "No output or attendance recorded for this period."}
                    </td></tr>
                  )}
                </tbody>
                {(data.rows || []).length > 0 && (
                  <tfoot>
                    <tr className="bg-slate-50 font-semibold">
                      <td className="font-heading">TOTAL</td>
                      {mode === "month" && <td></td>}
                      <td className="text-right font-mono">{fmt(data.totals?.output)}</td>
                      <td className="text-right font-mono">{fmt(data.totals?.machine_ops)}</td>
                      <td className="text-right font-mono">{fmt(data.totals?.helpers)}</td>
                      <td className="text-right font-mono">{fmt(data.totals?.manpower)}</td>
                      <td className="text-right font-mono text-brand">{data.totals?.ppmd != null ? fmt(data.totals.ppmd) : "—"}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </Card>

          {mode === "month" && lineId !== "__all__" && (data.per_day || []).length > 0 && (
            <>
              <Card className="border-slate-200 rounded-md bg-white overflow-hidden" data-testid="ppmd-trend-card">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div className="font-heading font-semibold text-[15px] flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-brand" />
                    Daily PPMD Trend · {(lines || []).find((l) => l.id === lineId)?.name || "Line"}
                  </div>
                  <div className="text-[11.5px] text-slate-500">Missing days = 0 output or 0 manpower</div>
                </div>
                <div className="p-4">
                  <div className="h-64 w-full" data-testid="ppmd-trend-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.per_day.map((r) => ({ date: r.date.slice(5), ppmd: r.ppmd ?? 0, output: r.output, manpower: r.manpower }))} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
                        <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={true} />
                        <Tooltip
                          contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E5E7EB" }}
                          formatter={(v, name) => [Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }), name === "ppmd" ? "PPMD" : name]}
                          labelFormatter={(l) => `Date: ${year}-${l}`}
                        />
                        <RLine type="monotone" dataKey="ppmd" stroke="#7C1D3A" strokeWidth={2.5} dot={{ r: 3, fill: "#7C1D3A" }} activeDot={{ r: 5 }} name="PPMD" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  {(() => {
                    const valid = data.per_day.filter((r) => r.ppmd != null);
                    if (valid.length === 0) return null;
                    const best = valid.reduce((a, b) => (a.ppmd >= b.ppmd ? a : b));
                    const worst = valid.reduce((a, b) => (a.ppmd <= b.ppmd ? a : b));
                    const avg = valid.reduce((s, r) => s + r.ppmd, 0) / valid.length;
                    return (
                      <div className="mt-3 grid grid-cols-3 gap-3 text-[12px]">
                        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5">
                          <div className="text-emerald-700 font-semibold text-[10.5px] uppercase tracking-wide">Best day</div>
                          <div className="font-mono font-bold text-emerald-800 mt-0.5">{fmt(best.ppmd)} <span className="text-[11px] font-normal text-emerald-600">on {best.date}</span></div>
                        </div>
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
                          <div className="text-slate-600 font-semibold text-[10.5px] uppercase tracking-wide">Avg PPMD</div>
                          <div className="font-mono font-bold text-slate-800 mt-0.5">{fmt(avg)}</div>
                        </div>
                        <div className="rounded-md border border-rose-200 bg-rose-50 p-2.5">
                          <div className="text-rose-700 font-semibold text-[10.5px] uppercase tracking-wide">Weakest day</div>
                          <div className="font-mono font-bold text-rose-800 mt-0.5">{fmt(worst.ppmd)} <span className="text-[11px] font-normal text-rose-600">on {worst.date}</span></div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </Card>

              <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 font-heading font-semibold text-[15px]">
                Day-by-day breakdown · {(lines || []).find((l) => l.id === lineId)?.name || "Line"}
              </div>
              <div className="overflow-x-auto">
                <table className="opsiq-table" data-testid="ppmd-per-day-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th className="text-right">Output</th>
                      <th className="text-right">Machine Ops</th>
                      <th className="text-right">Helpers</th>
                      <th className="text-right">Manpower</th>
                      <th className="text-right">PPMD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_day.map((r) => (
                      <tr key={r.date}>
                        <td className="font-mono text-[12.5px]">{r.date}</td>
                        <td className="text-right font-mono">{fmt(r.output)}</td>
                        <td className="text-right font-mono text-blue-700">{fmt(r.machine_ops)}</td>
                        <td className="text-right font-mono text-amber-700">{fmt(r.helpers)}</td>
                        <td className="text-right font-mono">{fmt(r.manpower)}</td>
                        <td className="text-right font-mono font-bold text-brand">{r.ppmd != null ? fmt(r.ppmd) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
